// FundDetail.jsx
import React, { useState, useEffect } from 'react';
import { 
    Building2, 
    Globe, 
    Clock,
    Calendar,
    DollarSign,
    BarChart3,
    Percent,
    Lock,
    Clock4,
    Users,
    Scale,
    FileText,
    Target
} from 'lucide-react';
import { useParams } from "react-router";
import './FundDetailPage.css';
import { formatCurrency, formatDateIntl, formatInterest } from "../../util/currency_lookup";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader } from '../../components/loader';

const FundDetailPage = () => {
  const [fund, setFund] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [allocationData, setAllocationData] = useState([]);
  const [loading, setLoading] = useState({
    fund: true,
    performance: true,
    allocation: true,
  });
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    fetchFund();
    fetchPerformanceData();
    fetchAllocationData();
  }, [id]);

  const fetchFund = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/by-id/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch fund');
      }

      const data = await response.json();
      setFund(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, fund: false }));
    }
  };

  const fetchPerformanceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/historical-returns/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch performance data');
      }

      const data = await response.json();
      setPerformanceData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, performance: false }));
    }
  };

  const fetchAllocationData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/asset-allocation/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch performance data');
      }

      const data = await response.json();
      console.log(data);
      setAllocationData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, allocation: false }));
    }
  };

  const isLoading = Object.values(loading).some(status => status);

  if (isLoading) {
    return <Loader loading={true} />;
  }

  if (error) return <div className="error">{error}</div>;
  if (!fund) return <div className="error">No fund found</div>;

  return (
    <div className="fund-detail">
      <div className="header">
        <div className="header-content">
          <h1>{fund.name}</h1>
          <div className="fund-symbol">{fund.symbol}</div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="overview">
        <div className="overview-card">
          <div className="overview-title">Fund Information</div>
          <div className="details-grid">
            <div className="detail-card">
              <div className="detail-card-content">
                <DollarSign className="detail-icon" />
                <div>
                  <div className="detail-label">Currency</div>
                  <div className="detail-value">{fund.currency_code}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <DollarSign className="detail-icon" />
                <div>
                  <div className="detail-label">Minimum Investment</div>
                  <div className="detail-value">{formatCurrency(fund.min_value, fund.currency_code)}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <DollarSign className="detail-icon" />
                <div>
                  <div className="detail-label">Minimum Additional</div>
                  <div className="detail-value">{formatCurrency(fund.min_addition_value, fund.currency_code)}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Clock className="detail-icon" />
                <div>
                  <div className="detail-label">Compounding Frequency</div>
                  <div className="detail-value">{fund.compounding_frequency}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div className="details-section">
          <div className="details-grid">
          <div className="detail-card">
            <div className="detail-item">
              <Clock4 className="detail-icon" />
              <div>
                <div className="detail-label">Distribution Frequency</div>
                <div className="detail-value">{fund.distribution_frequency}</div>
              </div>
            </div>
          </div>
          <div className="detail-card">
            <div className="detail-item">
              <Lock className="detail-icon" />
              <div>
                <div className="detail-label">Lock-in Period</div>
                <div className="detail-value">{fund.lock_in_period_days} days</div>
              </div>
            </div>
          </div>
          <div className="detail-card">
            <div className="detail-item">
              <Clock className="detail-icon" />
              <div>
                <div className="detail-label">Withdrawal Turnaround</div>
                <div className="detail-value">{fund.withdrawl_turnaround_days} days</div>
              </div>
            </div>
          </div>
          </div>
      </div>

      {/* Fund Partners */}
      <div className="partners-section">
        <h2>Fund Partners</h2>
        <div className="partners-grid">
          <div className="partner-card">
            <Users className="partner-icon" />
            <div>
              <div className="partner-label">Trustee</div>
              <div className="partner-value">{fund.trustee}</div>
            </div>
          </div>
          <div className="partner-card">
            <Building2 className="partner-icon" />
            <div>
              <div className="partner-label">Custodian</div>
              <div className="partner-value">{fund.custodian}</div>
            </div>
          </div>
          <div className="partner-card">
            <FileText className="partner-icon" />
            <div>
              <div className="partner-label">Auditor</div>
              <div className="partner-value">{fund.auditor}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Fees */}
      <div className="fees-section">
        <h2>Fees</h2>
        <div className="fees-grid">
          <div className="fee-card">
            <Percent className="fee-icon" />
            <div>
              <div className="fee-label">Management Fee</div>
              <div className="fee-value">{fund.management_fee+'%'}</div>
            </div>
          </div>
          <div className="fee-card">
            <Percent className="fee-icon" />
            <div>
              <div className="fee-label">Initial Fee</div>
              <div className="fee-value">{fund.initial_fee+'%'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      <div className="performance-section">
        <h2>Fund Performance</h2>
        <div className="benchmark-info">
          <Target className="benchmark-icon" />
          <span>Benchmark: {fund.bench_mark}</span>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={performanceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="rate_start_date" 
              tickFormatter={(date) => formatDateIntl(date, 'short')}
            />
            <YAxis 
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip 
              formatter={(value) => `${value}%`}
              labelFormatter={(date) => formatDateIntl(date)}
            />
            <Line 
              type="monotone" 
              dataKey="interest_rate" 
              stroke="#f87171" 
              name="Benchmark Return"
              strokeDasharray="5 5"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Asset Allocation */}
      <div className="allocation-section">
        <h2>Asset Allocation</h2>
        <div className="allocation-grid">
          {allocationData.map((allocation, index) => (
            <div key={index} className="allocation-card">
              <div className="allocation-name">{allocation.asset}</div>
              <div className="allocation-value">{formatInterest(allocation.allocation)}</div>
              <div className="allocation-bar">
                <div 
                  className="allocation-fill" 
                  style={{ width: `${allocation.allocation}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FundDetailPage;
